// crmRouteAccess — route → claim matrix. THE single source of truth for the
// shell's route guard AND the rail (top-level items and sub-items alike), so the
// rail never links to a page that would only render Access Denied.
//
// Spec: CLM-07b. Replaces CrmShell's role-only ROUTE_ACCESS.
//
// ── HOW A RULE IS READ ─────────────────────────────────────────────────────
// `pattern` is a react-router path matched as a PREFIX (`matchPath`, end:false),
// and the FIRST matching rule wins — so specific rules come before the general
// ones they sit under (`/quotes/settings` before `/quotes`). `claims` is anyOf.
//
// `legacyRoles` is the OLD role list for that prefix, verbatim, and is used only
// while the token has no `crm_perms_v` (see `crmClaims.ts`). `undefined` means
// the old matrix did not list the path, i.e. it was open to every signed-in
// operator. It goes when the rollout ends; the claim column is the contract.
//
// ── EVERY SHELL ROUTE MUST BE COVERED ──────────────────────────────────────
// `__tests__/crmRouteAccess.test.ts` walks every pattern the router serves and
// fails on any route that is neither matched by a rule nor listed in
// `OPEN_ROUTES`. A new page cannot ship ungated by accident.

import { matchPath } from 'react-router-dom';

import { hasClaim, isLegacyPrincipal, type ClaimPrincipal, type CrmClaim } from './crmClaims';

export interface RouteAccessRule {
  readonly pattern: string;
  readonly claims: readonly CrmClaim[];
  readonly legacyRoles?: readonly string[];
}

// Old-matrix role lists, named once so each legacy column reads as "what this
// prefix allowed before claims".
const L_QUOTES = ['admin', 'manager', 'quotes', 'viewer'] as const;
const L_BILLING = ['admin', 'manager', 'billing', 'viewer'] as const;
const L_ACCOUNTS = ['admin', 'manager', 'accounts', 'quotes', 'viewer'] as const;
const L_TARIFFS = ['admin', 'manager', 'pricing', 'user'] as const;
const L_PRICING = ['admin', 'manager', 'pricing'] as const;
const L_METERING = ['admin', 'manager', 'metering', 'viewer'] as const;
const L_ADMIN = ['admin'] as const;
const L_OPS = ['admin', 'ops'] as const;

export const ROUTE_ACCESS: readonly RouteAccessRule[] = [
  // ── Users ────────────────────────────────────────────────────────────────
  { pattern: '/users/audit', claims: ['users.audit.view'], legacyRoles: L_ADMIN },
  // A delegated assigner (only `users.claims.manage`) must reach the list to
  // open the Manage claims panel; Accounts serves GET /v1/us/users to either
  // claim. Invite, suspend and remove stay on `users.records.manage` on the page.
  {
    pattern: '/users',
    claims: ['users.records.view', 'users.claims.manage'],
    legacyRoles: L_ADMIN,
  },

  // ── Tariff management ────────────────────────────────────────────────────
  // The Users tab grants and revokes the tariff-manager capability: assigning a
  // capability to an operator is a claims act, not a pricing one.
  { pattern: '/tariffs/users', claims: ['users.claims.manage'], legacyRoles: L_ADMIN },
  { pattern: '/tariffs', claims: ['pricing.catalog.view'], legacyRoles: L_TARIFFS },
  // The old address of the catalogue: it only redirects to `/tariffs`, and the
  // shell gates BEFORE the redirect mounts, so it admits whoever /tariffs does.
  { pattern: '/pricing/tariffs', claims: ['pricing.catalog.view'], legacyRoles: L_TARIFFS },
  // Pricing Config is a write surface (rate cards, pricing config).
  { pattern: '/pricing/config', claims: ['pricing.catalog.manage'], legacyRoles: L_PRICING },
  { pattern: '/pricing', claims: ['pricing.catalog.view'], legacyRoles: L_PRICING },

  // ── Contracts and Signups ────────────────────────────────────────────────
  { pattern: '/quotes/settings', claims: ['quotes.settings.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/new', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/quick', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/marketplace', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/shop', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/bulk', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  { pattern: '/quotes/:quoteId/edit', claims: ['quotes.records.manage'], legacyRoles: L_QUOTES },
  // Capturing the signature submits the contract: the approve claim's act.
  { pattern: '/quotes/:quoteId/sign', claims: ['quotes.contracts.approve'], legacyRoles: L_QUOTES },
  { pattern: '/quotes', claims: ['quotes.records.view'], legacyRoles: L_QUOTES },

  // ── Billing and Payments ─────────────────────────────────────────────────
  // Every Billing page is readable with `billing.bills.view`; the writes on
  // them (generate, settings, scheduler) are gated per control.
  { pattern: '/billing', claims: ['billing.bills.view'], legacyRoles: L_BILLING },
  {
    pattern: '/payment/queue',
    claims: ['payments.records.view'],
    legacyRoles: ['admin', 'manager', 'billing', 'ops', 'viewer'],
  },
  {
    pattern: '/payment',
    claims: ['payments.records.view'],
    legacyRoles: ['admin', 'manager', 'billing', 'ops'],
  },

  // ── Accounts, Brokers, Prospects ─────────────────────────────────────────
  { pattern: '/accounts/new', claims: ['accounts.records.manage'], legacyRoles: L_ACCOUNTS },
  { pattern: '/accounts', claims: ['accounts.records.view'], legacyRoles: L_ACCOUNTS },
  { pattern: '/brokers', claims: ['brokers.records.view'], legacyRoles: L_ACCOUNTS },
  { pattern: '/prospects', claims: ['prospects.records.view'], legacyRoles: L_ACCOUNTS },

  // ── Metering and Market Operations ───────────────────────────────────────
  { pattern: '/metering', claims: ['metering.meters.view'], legacyRoles: L_METERING },
  { pattern: '/ops/metering', claims: ['metering.meters.view'], legacyRoles: L_METERING },
  { pattern: '/ops/market', claims: ['marketops.console.view'], legacyRoles: L_OPS },
  // Unlisted in the old matrix; its page refused everyone but ops/admin.
  { pattern: '/ops/enrollment-issues', claims: ['marketops.console.view'], legacyRoles: L_OPS },

  // ── Reports and Settings ─────────────────────────────────────────────────
  {
    pattern: '/reports',
    claims: ['reports.all.view'],
    legacyRoles: ['admin', 'manager', 'billing'],
  },
  // `/settings` itself stays open (its General tab holds Sign out); only the
  // Templates tab and its route need the claim.
  { pattern: '/settings/templates', claims: ['settings.templates.manage'], legacyRoles: L_ADMIN },
];

/**
 * Shell routes that need no claim (catalogue: "`/dashboard`, `/settings`,
 * `/help` and `/docs` need no claim"). Listed so the coverage test can tell a
 * deliberate open route from a forgotten one.
 */
export const OPEN_ROUTES: readonly string[] = [
  '/',
  '/dashboard',
  '/settings',
  '/help',
  '/help/:sectionId',
  '/docs',
];

/** Is `pathname` exactly one of the deliberately open routes? */
export function isOpenRoute(pathname: string): boolean {
  return OPEN_ROUTES.some((p) => matchPath({ path: p, end: true }, pathname) !== null);
}

/** The first rule whose pattern prefixes `pathname`, or undefined. */
export function routeRuleFor(pathname: string): RouteAccessRule | undefined {
  return ROUTE_ACCESS.find((r) => matchPath({ path: r.pattern, end: false }, pathname) !== null);
}

/**
 * May this principal open `pathname`?
 *
 * DEFAULT DENY (Wave 4 audit): a path is open only if it is listed EXACTLY in
 * `OPEN_ROUTES`; otherwise it needs a matching `ROUTE_ACCESS` rule, and a path
 * with neither is refused. A new page that forgot its rule renders Access
 * Denied instead of opening to everyone. A legacy token (no `crm_perms_v`) is
 * judged by the rule's old role list; every other principal by its claims.
 */
export function pathAllowed(pathname: string, principal: ClaimPrincipal | null): boolean {
  if (principal === null) return false;
  const rule = routeRuleFor(pathname);
  if (rule === undefined) return isOpenRoute(pathname);
  if (isLegacyPrincipal(principal)) {
    const legacy = rule.legacyRoles;
    if (legacy === undefined) return true;
    const roles: readonly string[] = principal.roles;
    return legacy.some((r) => roles.includes(r));
  }
  return hasClaim(principal, rule.claims);
}

/** A rail entry: a destination and optional children. */
export interface GatedNavItem {
  readonly to: string;
  readonly sub?: ReadonlyArray<{ readonly to: string }>;
}

/**
 * The rail as the principal may see it: sub-items they cannot open are removed,
 * and an item is kept only if its own destination is allowed or — for a parent
 * whose click only toggles its submenu — at least one child survives.
 */
export function filterNavItems<T extends GatedNavItem>(
  items: readonly T[],
  principal: ClaimPrincipal | null,
): T[] {
  const out: T[] = [];
  for (const item of items) {
    if (item.sub === undefined) {
      if (pathAllowed(item.to, principal)) out.push(item);
      continue;
    }
    const sub = item.sub.filter((s) => pathAllowed(s.to, principal));
    if (sub.length > 0) out.push({ ...item, sub });
  }
  return out;
}
